import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Tooltip from '@mui/joy/Tooltip';
import CircularProgress from '@mui/joy/CircularProgress';
import LineshapeParameterModal from './LineshapeParameterModal';
import { API_PATH, handleApiResponse } from '../Internal/api.js';

export default function LineshapeSelector(props) {
    const { value, onChange, locked, toolTip } = props;
    const [toolTipLineshape, setToolTipLineshape] = useState(true);
    const [paramModal, setParamModal] = useState(false);
    const [selectedLineshape, setSelectedLineshape] = useState(null);
    
    const dispatch = useDispatch();
    const lineshapeSchemas = useSelector(state => state.lineshapeSchemas.schemas);
    const currentBackend = useSelector(state => state.backend.currentBackend);
    // Fetch lineshape schemas from API on first load
    const api_query = API_PATH + "lineshapes" + "?backend=" + currentBackend;
    useEffect(() => {
        if (lineshapeSchemas !== null) {
            return;
        }
        fetch(api_query)
        .then(response => handleApiResponse(response, dispatch))
        .then(data => {
            dispatch({ type: 'SET_LINESHAPE_SCHEMAS', payload: data });
        })
        .catch(error => {
            console.error('Error fetching lineshape schemas:', error);
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    
    // Dynamically generate lineshape options from schema
    const lineshapeOptions = [
        { value: 'none', label: 'None (Default)', schema: null }
    ];
    
    // Add all lineshapes from the schema
    if (lineshapeSchemas && lineshapeSchemas.lineshapes) {
        Object.entries(lineshapeSchemas.lineshapes).forEach(([key, schema]) => {
            lineshapeOptions.push({
                value: schema.lineshape_type,
                label: schema.lineshape_type.replace(/([A-Z])/g, ' $1').trim(), // Add spaces before capitals
                schema: schema
            });
        });
    }

    const handleLineshapeSelect = (event, newValue) => {
        if (newValue === 'none' || newValue === null) {
            // Return null for default case
            onChange(event, null);
            setSelectedLineshape(null);
        } else {
            // Find the schema for the selected lineshape
            const selected = lineshapeOptions.find(opt => opt.value === newValue);
            if (selected && selected.schema) {
                setSelectedLineshape(selected.schema);
                setParamModal(true);
            }
        }
    };

    const handleParamModalClose = () => {
        setParamModal(false);
        // Reset to previous value or null if cancelled
        if (!value) {
            setSelectedLineshape(null);
        }
    };

    const handleParamSave = (lineshapeParams) => {
        onChange(null, lineshapeParams);
        setParamModal(false);
    };

    // Get display value for the selector
    const displayValue = value ? value.lineshape_type : 'none';

    // Check if schemas are loading
    const isLoading = lineshapeSchemas === null;

    return (
        <>
            <Tooltip title={toolTipLineshape ? toolTip : null}>
                <Select 
                    value={displayValue} 
                    onChange={handleLineshapeSelect} 
                    disabled={locked || isLoading}
                    placeholder={isLoading ? "Loading lineshapes..." : "Lineshape (Optional)"} 
                    onListboxOpenChange={() => setToolTipLineshape(!toolTipLineshape)}
                    endDecorator={
                        isLoading ? (
                          <CircularProgress size="sm" sx={{ bgcolor: 'background.surface' }} />
                        ) : null
                    }
                >
                    {lineshapeOptions.map((option) => (
                        <Option key={option.value} value={option.value}>
                            {option.label}
                        </Option>
                    ))}
                </Select>
            </Tooltip>

            {/* Parameter Configuration Modal */}
            <LineshapeParameterModal
                open={paramModal}
                onClose={handleParamModalClose}
                onSave={handleParamSave}
                lineshapeSchema={selectedLineshape}
                initialValues={value}
            />
        </>
    );
}

